/**
 * =========================================================================================
 * SHERGOLD PRIME ERP - PRINT MODEL ASSIGNER & VOUCHER ROUTING HUB
 * =========================================================================================
 *
 * COMPONENT OVERVIEW:
 * A visual ERP tool for mapping print models (both custom database templates and standard
 * factory presets) to transaction vouchers dynamically loaded from `FormRegistration`.
 *
 * DESIGN PATTERN:
 * Matches the luxury 2-column card selector layout of `AppConfigMaster.jsx`:
 * - Left Panel:
 *     - Showroom Branch Location Selector
 *     - Voucher Type Selector (dynamically loaded from FormRegistration)
 *     - 2-Column Luxury Template Card Selector with miniature receipt mockup,
 *       dimensions badge, active indicator, and smooth selection state.
 *     - Deep-link action button to open Invoice Designer.
 * - Right Panel:
 *     - Real-time Live PDF Preview rendered via @pdfme/generator BEFORE selection/assignment.
 *     - "Set as Active Model" 1-click assignment button.
 *     - Current showroom assignment status and unassign capability.
 * =========================================================================================
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import Swal from "sweetalert2";
import axios from "axios";
import { useDbContext } from "../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../app/permissionsSlice";
import { selectActiveLocation } from "../../../app/locationSlice";

// PDFME Preview Compilation Engine
import { generate } from "@pdfme/generator";
import { getDefaultFont } from "@pdfme/common";
import {
  text,
  image,
  line,
  rectangle,
  table,
  multiVariableText,
  barcodes
} from "@pdfme/schemas";

// Shared Presets & Token Helpers
import { SAMPLE_TEMPLATES } from "../InvoiceDesigner/sampleTemplates";
import { SAMPLE_DATA_MAP, resolveTemplateInputs } from "../InvoiceDesigner/systemTokens";
import { clearPrintTemplateCache, notifyPrintTemplateUpdated } from "../../../hooks/usePrintService";
import InvoiceDesignerManualModal from "../InvoiceDesigner/InvoiceDesignerManualModal";

// Icons
import {
  FiPrinter,
  FiHome,
  FiTag,
  FiFileText,
  FiRefreshCw,
  FiAward,
  FiCreditCard,
  FiCode,
  FiEye,
  FiCheck,
  FiTrash2,
  FiInfo,
  FiSearch,
  FiEdit3,
  FiExternalLink,
  FiPlus,
  FiMaximize2,
  FiCheckCircle,
  FiSliders,
  FiCheckSquare,
  FiChevronDown,
  FiChevronUp,
  FiX,
  FiHelpCircle
} from "react-icons/fi";

const INTER = { fontFamily: "Inter, sans-serif" };

// Registered @pdfme plugins
const plugins = {
  text,
  image,
  line,
  rectangle,
  table,
  multiVariableText,
  qrcode: barcodes.qrcode,
  ...barcodes
};

/**
 * Standard ERP Fallback Vouchers if FormRegistration is empty or offline
 */
const DEFAULT_VOUCHER_TYPES = [
  "SALE INVOICE",
  "ESTIMATE",
  "PURCHASE",
  "SCHEME RECEIPT",
  "SCHEME REDEEM",
  "RECEIPT",
  "PAYMENT",
  "GOLD RECEIPT",
  "SMITH RECEIPT",
  "BARCODE TAG"
];

/**
 * Category & Icon mapping helper for voucher types
 */
function getVoucherMeta(vType = "") {
  const upper = (vType || "").toUpperCase();
  if (upper.includes("SALE") || upper.includes("BILL")) {
    return { icon: FiTag, badge: "bg-blue-100 text-blue-800 border-blue-200", color: "#2563eb" };
  }
  if (upper.includes("ESTIMATE") || upper.includes("ORDER")) {
    return { icon: FiFileText, badge: "bg-indigo-100 text-indigo-800 border-indigo-200", color: "#4f46e5" };
  }
  if (upper.includes("PURCHASE") || upper.includes("GOLD") || upper.includes("REFINER")) {
    return { icon: FiRefreshCw, badge: "bg-teal-100 text-teal-800 border-teal-200", color: "#0d9488" };
  }
  if (upper.includes("SCHEME") || upper.includes("REDEEM")) {
    return { icon: FiAward, badge: "bg-amber-100 text-amber-800 border-amber-200", color: "#d97706" };
  }
  if (upper.includes("RECEIPT") || upper.includes("PAYMENT")) {
    return { icon: FiCreditCard, badge: "bg-emerald-100 text-emerald-800 border-emerald-200", color: "#059669" };
  }
  if (upper.includes("TAG") || upper.includes("BARCODE")) {
    return { icon: FiCode, badge: "bg-purple-100 text-purple-800 border-purple-200", color: "#7c3aed" };
  }
  return { icon: FiPrinter, badge: "bg-slate-100 text-slate-800 border-slate-200", color: "#475569" };
}

export default function PrintModelAssigner({ onClose, zIndex, onOpenDesigner }) {
  const { dbCode } = useDbContext();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  // Permissions
  const permissions = useSelector(selectMenuPermissions);
  const formPermissions = permissions?.["printModelAssigner"]?.actions || {};
  const hasSavePermission = formPermissions.Save ?? true;
  const hasEditPermission = formPermissions.Edit ?? true;
  const hasDeletePermission = formPermissions.Delete ?? true;
  const hasFindPermission = formPermissions.Find ?? true;

  // Locations / Showrooms state & Redux active showroom awareness
  const activeLocation = useSelector(selectActiveLocation);
  const currentLocId = Number(
    activeLocation?.id ||
    sessionStorage.getItem("loginLocationId") ||
    localStorage.getItem("loginLocationId") ||
    0
  );
  const [locationsList, setLocationsList] = useState([{ id: 0, name: "Global Showroom (All Branches)" }]);
  const [selectedLocation, setSelectedLocation] = useState(() => currentLocId);

  // Sync selectedLocation when activeLocation loads from Redux
  useEffect(() => {
    if (activeLocation?.id && (!selectedLocation || selectedLocation === 0)) {
      setSelectedLocation(Number(activeLocation.id));
    }
  }, [activeLocation]);

  // Voucher Types loaded dynamically from FormRegistration + custom additions
  const [voucherTypes, setVoucherTypes] = useState(DEFAULT_VOUCHER_TYPES);
  const [customVoucherTypes, setCustomVoucherTypes] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`shergold_custom_vouchers_${dbCode || "DEFAULT"}`) || "[]");
    } catch (_) {
      return [];
    }
  });
  const [registeredForms, setRegisteredForms] = useState([]);
  const [selectedVoucherType, setSelectedVoucherType] = useState(DEFAULT_VOUCHER_TYPES[0]);
  const [isLoadingVouchers, setIsLoadingVouchers] = useState(false);

  // Combobox editable text and dropdown open state
  const [voucherInputText, setVoucherInputText] = useState(DEFAULT_VOUCHER_TYPES[0]);
  const [isVoucherDropdownOpen, setIsVoucherDropdownOpen] = useState(false);
  const voucherComboboxRef = useRef(null);

  // Keep voucher input text in sync with selected voucher
  useEffect(() => {
    setVoucherInputText(selectedVoucherType || "");
  }, [selectedVoucherType]);

  // Click outside to close combobox dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (voucherComboboxRef.current && !voucherComboboxRef.current.contains(e.target)) {
        setIsVoucherDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Templates & Assignments state
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Template Search / Filter
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [paperFilter, setPaperFilter] = useState("all");

  // Selected Card for Live Preview
  const [selectedTemplateCard, setSelectedTemplateCard] = useState(null);

  // Live PDF Preview Engine state
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  // User Manual & Guide Modal State
  const [showManualModal, setShowManualModal] = useState(false);

  // Keep track of active blob URL for memory cleanup
  const currentBlobUrlRef = useRef(null);

  /**
   * Helper: Show toast notification
   */
  const showNotification = (msg, icon = "success") => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon,
      title: msg,
      showConfirmButton: false,
      timer: 2400,
      timerProgressBar: true
    });
  };

  /**
   * 1. Fetch Showroom Locations
   */
  const fetchLocations = useCallback(async () => {
    if (!apiBaseUrl || !dbCode) return;
    try {
      const res = await axios.get(`${apiBaseUrl}/main/location/all/${dbCode}`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        const mapped = res.data.map((loc) => ({
          id: Number(loc.id || loc.LocId || loc.LocationId),
          name: loc.name || loc.LocName || `Branch #${loc.id}`
        }));
        setLocationsList([{ id: 0, name: "Global (All Showrooms)" }, ...mapped]);
      }
    } catch (err) {
      console.warn("Could not fetch locations:", err.message);
    }
  }, [apiBaseUrl, dbCode]);

  /**
   * 2. Load Voucher Types Dynamically from FormRegistration
   */
  const fetchVoucherTypesFromDb = useCallback(async () => {
    setIsLoadingVouchers(true);
    let loadedTypes = [];
    let loadedForms = [];

    if (apiBaseUrl && dbCode) {
      try {
        const res = await axios.get(`${apiBaseUrl}/main/printtemplates/vouchers/${dbCode}`);
        if (res.data) {
          if (Array.isArray(res.data.types) && res.data.types.length > 0) {
            loadedTypes = res.data.types.map((t) => t.trim().toUpperCase());
          }
          if (Array.isArray(res.data.forms)) {
            loadedForms = res.data.forms;
          }
        }
      } catch (err) {
        console.warn("Could not fetch vouchers from FormRegistration endpoint:", err.message);
      }
    }

    // Read any custom voucher types from local storage
    const localKey = `shergold_custom_vouchers_${dbCode || "DEFAULT"}`;
    let localCustom = [];
    try {
      localCustom = JSON.parse(localStorage.getItem(localKey) || "[]");
    } catch (_) {}

    // Merge with DB types, custom types, and standard fallback types
    const combinedSet = new Set([...loadedTypes, ...localCustom, ...customVoucherTypes, ...DEFAULT_VOUCHER_TYPES]);
    const finalVouchers = Array.from(combinedSet).filter(Boolean);

    setVoucherTypes(finalVouchers);
    setRegisteredForms(loadedForms);
    setIsLoadingVouchers(false);

    // Keep selected or choose first
    setSelectedVoucherType((prev) => {
      if (prev && finalVouchers.includes(prev)) return prev;
      return finalVouchers[0] || "SALE INVOICE";
    });
  }, [apiBaseUrl, dbCode, customVoucherTypes]);

  /**
   * Add a new custom voucher type
   */
  const handleAddCustomVoucherType = async () => {
    const { value: customName } = await Swal.fire({
      title: "Add Custom Voucher Type",
      text: "Enter the name of the new ERP voucher type (e.g. WHOLESALE BILL, REPAIR ORDER, APPROVAL MEMO):",
      input: "text",
      inputPlaceholder: "e.g. WHOLESALE BILL",
      showCancelButton: true,
      confirmButtonText: "Add Voucher",
      confirmButtonColor: "#0f2d59",
      cancelButtonColor: "#64748b",
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "Please enter a voucher type name!";
        }
        const clean = value.trim().toUpperCase();
        if (voucherTypes.includes(clean)) {
          return "This voucher type already exists!";
        }
      }
    });

    if (customName) {
      const clean = customName.trim().toUpperCase();
      const updatedCustom = Array.from(new Set([...customVoucherTypes, clean]));
      setCustomVoucherTypes(updatedCustom);
      localStorage.setItem(`shergold_custom_vouchers_${dbCode || "DEFAULT"}`, JSON.stringify(updatedCustom));

      setVoucherTypes((prev) => Array.from(new Set([...prev, clean])));
      setSelectedVoucherType(clean);
      showNotification(`Added custom voucher "${clean}"`, "success");
    }
  };

  /**
   * Delete a custom voucher type
   */
  const handleDeleteCustomVoucherType = (vType) => {
    Swal.fire({
      title: `Delete "${vType}"?`,
      text: "This will remove this custom voucher type from your selector.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it"
    }).then((result) => {
      if (result.isConfirmed) {
        const updated = customVoucherTypes.filter((c) => c !== vType);
        setCustomVoucherTypes(updated);
        localStorage.setItem(`shergold_custom_vouchers_${dbCode || "DEFAULT"}`, JSON.stringify(updated));
        setVoucherTypes((prev) => prev.filter((v) => v !== vType));
        setSelectedVoucherType((prev) => (prev === vType ? voucherTypes.find((v) => v !== vType) || "SALE INVOICE" : prev));
        showNotification(`Removed custom voucher "${vType}"`, "info");
      }
    });
  };

  /**
   * Select or commit a voucher type (from clicking an option or pressing enter)
   */
  const handleSelectVoucherType = (vType) => {
    const clean = (vType || "").trim().toUpperCase();
    if (!clean) return;
    setSelectedVoucherType(clean);
    setVoucherInputText(clean);
    setIsVoucherDropdownOpen(false);

    // If it's a new custom voucher type not in current list, save it
    if (!voucherTypes.includes(clean)) {
      const updated = Array.from(new Set([...customVoucherTypes, clean]));
      setCustomVoucherTypes(updated);
      localStorage.setItem(`shergold_custom_vouchers_${dbCode || "DEFAULT"}`, JSON.stringify(updated));
      setVoucherTypes((prev) => Array.from(new Set([...prev, clean])));
      showNotification(`Added and selected custom voucher "${clean}"`, "success");
    }
  };

  /**
   * Filter voucher types for combobox dropdown matching typed text
   */
  const filteredDropdownVouchers = useMemo(() => {
    const q = (voucherInputText || "").trim().toLowerCase();
    if (!q) return voucherTypes;
    return voucherTypes.filter((v) => v.toLowerCase().includes(q));
  }, [voucherTypes, voucherInputText]);

  /**
   * 3. Fetch Custom Database Templates
   */
  const fetchTemplates = useCallback(async () => {
    let dbTemplates = [];
    if (apiBaseUrl && dbCode) {
      try {
        const res = await axios.get(`${apiBaseUrl}/main/printtemplates/all/${dbCode}?_t=${Date.now()}`);
        if (Array.isArray(res.data)) {
          dbTemplates = res.data.map((t) => ({
            id: t.Auto,
            templateName: t.TemplateName,
            voucherType: (t.VoucherType || "").trim().toUpperCase(),
            paperSize: t.PaperSize,
            widthMm: t.WidthMm || 58,
            heightMm: t.HeightMm || 130,
            paddingMm: t.PaddingMm || 2,
            templateJson: t.TemplateJson,
            createdAt: t.CreatedAt
          }));
        }
      } catch (err) {
        console.warn("Could not fetch templates from backend:", err.message);
      }
    }

    // Fallback merge with localStorage
    const localKey = `shergold_print_templates_${dbCode || "DEFAULT"}`;
    let localTemplates = [];
    try {
      localTemplates = JSON.parse(localStorage.getItem(localKey) || "[]");
    } catch (_) {}

    const merged = new Map();
    // 1. DB templates take precedence
    dbTemplates.forEach((t) => {
      const nameKey = (t.templateName || "").trim().toLowerCase();
      if (nameKey) merged.set(nameKey, t);
    });
    // 2. Add local-only templates if not in DB yet
    localTemplates.forEach((t) => {
      const nameKey = (t.templateName || "").trim().toLowerCase();
      if (nameKey && !merged.has(nameKey)) {
        merged.set(nameKey, t);
      }
    });

    setSavedTemplates(Array.from(merged.values()));
  }, [apiBaseUrl, dbCode]);

  /**
   * 4. Fetch Active Assignments for Selected Location
   */
  const fetchAssignments = useCallback(
    async (loc = selectedLocation) => {
      setIsLoadingData(true);
      if (!apiBaseUrl || !dbCode) {
        setIsLoadingData(false);
        return;
      }
      try {
        const res = await axios.get(`${apiBaseUrl}/main/printtemplates/assignments/${dbCode}?location=${loc}&_t=${Date.now()}`);
        const data = res.data || [];
        setAssignments(data);
        localStorage.setItem(`shergold_print_assignments_${dbCode}`, JSON.stringify(data));
      } catch (err) {
        console.warn("Could not fetch assignments:", err.message);
        const cached = localStorage.getItem(`shergold_print_assignments_${dbCode}`);
        if (cached) {
          try {
            setAssignments(JSON.parse(cached));
          } catch (_) {}
        }
      } finally {
        setIsLoadingData(false);
      }
    },
    [apiBaseUrl, dbCode, selectedLocation]
  );

  // Initial load
  useEffect(() => {
    fetchLocations();
    fetchVoucherTypesFromDb();
    fetchTemplates();
    fetchAssignments();
  }, [fetchLocations, fetchVoucherTypesFromDb, fetchTemplates, fetchAssignments]);

  // Real-time synchronization when templates or assignments change across tabs, modals, or Invoice Designer
  useEffect(() => {
    const handleUpdate = () => {
      clearPrintTemplateCache();
      fetchTemplates();
      fetchAssignments();
    };

    window.addEventListener("printTemplateUpdated", handleUpdate);
    window.addEventListener("storage", (e) => {
      if (
        e.key === "shergold_print_template_version" ||
        (e.key && (e.key.startsWith("shergold_print_templates") || e.key.startsWith("shergold_print_assignments")))
      ) {
        handleUpdate();
      }
    });

    let syncChannel = null;
    try {
      if (typeof BroadcastChannel !== "undefined") {
        syncChannel = new BroadcastChannel("shergold_print_sync");
        syncChannel.onmessage = (event) => {
          if (event.data?.type === "printTemplateUpdated") {
            handleUpdate();
          }
        };
      }
    } catch (_) {}

    return () => {
      window.removeEventListener("printTemplateUpdated", handleUpdate);
      if (syncChannel) {
        try {
          syncChannel.close();
        } catch (_) {}
      }
    };
  }, [fetchTemplates, fetchAssignments]);

  /**
   * Identify currently assigned model for (selectedLocation, selectedVoucherType)
   * Priority: Exact Showroom Branch match > Global Fallback (Location: 0)
   */
  const activeAssignment = useMemo(() => {
    if (!selectedVoucherType) return null;

    // 1. Exact Showroom Branch assignment match
    const exactMatch = assignments.find(
      (a) =>
        (a.VoucherType || "").trim().toUpperCase() === selectedVoucherType.toUpperCase() &&
        Number(a.Location) === Number(selectedLocation)
    );
    if (exactMatch) {
      return {
        ...exactMatch,
        isBranchOverride: Number(selectedLocation) !== 0
      };
    }

    // 2. Fallback to Global (Location: 0) if a branch is currently selected
    if (Number(selectedLocation) !== 0) {
      const globalMatch = assignments.find(
        (a) =>
          (a.VoucherType || "").trim().toUpperCase() === selectedVoucherType.toUpperCase() &&
          Number(a.Location) === 0
      );
      if (globalMatch) {
        return {
          ...globalMatch,
          isInheritedFromGlobal: true
        };
      }
    }

    return null;
  }, [assignments, selectedVoucherType, selectedLocation]);

  /**
   * Gather all selectable templates for the selected voucher type:
   * 1. Custom DB templates matching this voucher type (or general)
   * 2. Factory presets matching this voucher type or relevant paper sizes
   */
  const availableTemplatesForVoucher = useMemo(() => {
    const list = [];
    const vUpper = (selectedVoucherType || "").toUpperCase();

    // 1. Custom templates from DB
    savedTemplates.forEach((t) => {
      const tVoucher = (t.voucherType || "").trim().toUpperCase();
      const isAssignedToThisVoucher = assignments.some(
        (a) => (a.VoucherType || "").trim().toUpperCase() === vUpper && Number(a.TemplateId) === Number(t.id)
      );
      const matchesVoucher = !tVoucher || tVoucher === vUpper || tVoucher === "ALL" || isAssignedToThisVoucher;
      if (matchesVoucher) {
        let parsed = null;
        if (t.templateJson) {
          try {
            parsed = typeof t.templateJson === "string" ? JSON.parse(t.templateJson) : t.templateJson;
          } catch (_) {}
        }
        list.push({
          id: t.id,
          numericId: t.id,
          name: t.templateName,
          voucherType: t.voucherType || selectedVoucherType,
          paperSize: t.paperSize || "A4",
          widthMm: t.widthMm || 210,
          heightMm: t.heightMm || 297,
          isPreset: false,
          sourceLabel: "Custom Model",
          createdAt: t.createdAt,
          schema: parsed,
          bgGradient:
            t.paperSize?.includes("58") || t.paperSize?.includes("57") || t.paperSize?.includes("80")
              ? "linear-gradient(145deg, #f8fafc 0%, #f1f5f9 60%, #e2e8f0 100%)"
              : "linear-gradient(145deg, #ffffff 0%, #fcfbf9 50%, #f5efe6 100%)"
        });
      }
    });

    // 2. Factory Presets from SAMPLE_TEMPLATES
    Object.entries(SAMPLE_TEMPLATES).forEach(([pKey, pObj]) => {
      // If a custom template already exists with this exact name, do not show the duplicate unedited preset
      const alreadyInCustom = list.some(
        (customItem) => (customItem.name || "").trim().toLowerCase() === (pObj.name || "").trim().toLowerCase()
      );
      if (alreadyInCustom) {
        return;
      }

      const presetVoucher = (pObj.voucherType || "").toUpperCase();
      let isRelevant = false;

      // Direct match or contextual relevance
      if (presetVoucher === vUpper) {
        isRelevant = true;
      } else if (vUpper.includes("SCHEME") && (pKey.includes("scheme") || pKey.includes("passbook") || pKey.includes("pos"))) {
        isRelevant = true;
      } else if (vUpper.includes("SALE") && (pKey.includes("Sales") || pKey.includes("Jewel") || pKey.includes("pos"))) {
        isRelevant = true;
      } else if (vUpper.includes("PURCHASE") && (pKey.includes("OldGold") || pKey.includes("Sales") || pKey.includes("Jewel"))) {
        isRelevant = true;
      } else if (vUpper.includes("RECEIPT") && (pKey.toLowerCase().includes("receipt") || pKey.includes("pos") || pKey.includes("scheme"))) {
        isRelevant = true;
      } else if (vUpper.includes("PAYMENT") && (pKey.toLowerCase().includes("payment") || pKey.includes("pos"))) {
        isRelevant = true;
      } else if (vUpper.includes("TAG") && pKey.includes("barcode")) {
        isRelevant = true;
      } else if (!list.some((item) => item.isPreset)) {
        // Fallback: show common presets if nothing else is relevant
        isRelevant = true;
      }

      if (isRelevant) {
        list.push({
          id: `preset_${pKey}`,
          presetKey: pKey,
          numericId: null, // Will be resolved or inserted on assign
          name: pObj.name,
          voucherType: selectedVoucherType,
          paperSize: pObj.paperSize || (pObj.width <= 58 ? "58MM" : pObj.width <= 80 ? "80MM" : (pObj.width <= 148 || pObj.height <= 148) ? "A5" : "A4"),
          widthMm: pObj.width || 210,
          heightMm: pObj.height || 297,
          isPreset: true,
          sourceLabel: "Factory Preset",
          schema: pObj.template,
          bgGradient:
            pObj.paperSize?.includes("58") || pObj.paperSize?.includes("57") || pObj.paperSize?.includes("80")
              ? "linear-gradient(145deg, #f8fafc 0%, #f1f5f9 60%, #e2e8f0 100%)"
              : "linear-gradient(145deg, #ffffff 0%, #fcfbf9 50%, #f5efe6 100%)"
        });
      }
    });

    return list;
  }, [savedTemplates, selectedVoucherType]);

  /**
   * Filtered templates according to search & paper filter
   */
  const filteredTemplates = useMemo(() => {
    return availableTemplatesForVoucher.filter((t) => {
      const q = templateSearchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        t.name.toLowerCase().includes(q) ||
        t.paperSize.toLowerCase().includes(q) ||
        t.sourceLabel.toLowerCase().includes(q);

      const matchesPaper =
        paperFilter === "all" ||
        (paperFilter === "thermal" && (t.paperSize.includes("58") || t.paperSize.includes("57") || t.paperSize.includes("80"))) ||
        (paperFilter === "sheet" && (t.paperSize.includes("A4") || t.paperSize.includes("A5") || t.paperSize.includes("Letter"))) ||
        (paperFilter === "tag" && (t.paperSize.includes("TAG") || t.paperSize.includes("BARCODE")));

      return matchesSearch && matchesPaper;
    });
  }, [availableTemplatesForVoucher, templateSearchQuery, paperFilter]);

  /**
   * Synchronize Selected Template Card:
   * When voucher changes or templates load, select currently active assigned model,
   * or default to the first available template.
   */
  useEffect(() => {
    if (!filteredTemplates || filteredTemplates.length === 0) {
      setSelectedTemplateCard(null);
      setPreviewPdfUrl(null);
      return;
    }

    // Try to match active assignment
    if (activeAssignment?.TemplateId) {
      const foundActive = filteredTemplates.find(
        (t) => Number(t.numericId) === Number(activeAssignment.TemplateId)
      );
      if (foundActive) {
        setSelectedTemplateCard(foundActive);
        return;
      }
    }

    // Otherwise keep current if still in list, or select first
    setSelectedTemplateCard((prev) => {
      if (prev) {
        const stillExists = filteredTemplates.find(
          (t) => t.id === prev.id || (t.numericId && prev.numericId && Number(t.numericId) === Number(prev.numericId))
        );
        if (stillExists) return stillExists;
      }
      return filteredTemplates[0];
    });
  }, [filteredTemplates, activeAssignment]);

  /**
   * Real-time PDF Compilation for Live Preview Canvas
   */
  useEffect(() => {
    let isCancelled = false;

    if (!selectedTemplateCard || !selectedTemplateCard.schema) {
      setPreviewPdfUrl(null);
      setPreviewError(null);
      return;
    }

    const compilePdf = async () => {
      setIsPreviewLoading(true);
      setPreviewError(null);

      try {
        const schema = selectedTemplateCard.schema;
        const inputs = resolveTemplateInputs(schema, SAMPLE_DATA_MAP);

        const pdfBytes = await generate({
          template: schema,
          inputs,
          plugins,
          options: {
            font: getDefaultFont()
          }
        });

        if (isCancelled) return;

        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);

        // Revoke previous URL to prevent memory leaks
        if (currentBlobUrlRef.current) {
          URL.revokeObjectURL(currentBlobUrlRef.current);
        }
        currentBlobUrlRef.current = url;
        setPreviewPdfUrl(url);
      } catch (err) {
        if (!isCancelled) {
          console.error("PDF preview generation failed:", err);
          setPreviewError(err.message || "Failed to render PDF preview");
          setPreviewPdfUrl(null);
        }
      } finally {
        if (!isCancelled) {
          setIsPreviewLoading(false);
        }
      }
    };

    compilePdf();

    return () => {
      isCancelled = true;
    };
  }, [selectedTemplateCard]);

  // Clean up object URL when component unmounts
  useEffect(() => {
    return () => {
      if (currentBlobUrlRef.current) {
        URL.revokeObjectURL(currentBlobUrlRef.current);
      }
    };
  }, []);

  /**
   * 1-Click Assignment Action:
   * Sets the currently selected card as the active model for (selectedLocation, selectedVoucherType)
   */
  const handleAssignCurrentTemplate = async () => {
    if (!selectedTemplateCard) return;
    if (!hasSavePermission && !hasEditPermission) {
      showNotification("You do not have permission to assign print models.", "warning");
      return;
    }
    if (!apiBaseUrl || !dbCode) {
      showNotification("Cannot assign: no database connection configured.", "error");
      return;
    }
    setIsAssigning(true);

    try {
      let numericId = selectedTemplateCard.numericId;

      // Check if numericId is already a valid positive integer from DB
      const parsedNum = Number(numericId);
      const isPersistedInDb = !isNaN(parsedNum) && parsedNum > 0 && !String(numericId).startsWith("local_");

      if (!isPersistedInDb) {
        // Needs to be inserted into DB first (factory preset or custom local template)
        let templateJsonStr = "";

        if (selectedTemplateCard.schema) {
          // schema is already parsed object from availableTemplatesForVoucher
          templateJsonStr = typeof selectedTemplateCard.schema === "string"
            ? selectedTemplateCard.schema
            : JSON.stringify(selectedTemplateCard.schema);
        } else if (selectedTemplateCard.isPreset && selectedTemplateCard.presetKey) {
          // factory preset – pull from SAMPLE_TEMPLATES
          const presetObj = SAMPLE_TEMPLATES[selectedTemplateCard.presetKey];
          if (presetObj?.template) {
            templateJsonStr = JSON.stringify(presetObj.template);
          }
        } else {
          // Local template whose schema failed to parse — read raw string from localStorage
          const localKey = `shergold_print_templates_${dbCode || "DEFAULT"}`;
          try {
            const localList = JSON.parse(localStorage.getItem(localKey) || "[]");
            const localMatch = localList.find(
              (t) => (t.templateName || "").trim().toLowerCase() === (selectedTemplateCard.name || "").trim().toLowerCase()
            );
            if (localMatch?.templateJson) {
              templateJsonStr = typeof localMatch.templateJson === "string"
                ? localMatch.templateJson
                : JSON.stringify(localMatch.templateJson);
            }
          } catch (_) {}
        }

        // Check if an existing template with same name already exists in DB (avoid duplicate insert)
        const existing = savedTemplates.find(
          (t) => (t.templateName || "").trim().toLowerCase() === (selectedTemplateCard.name || "").trim().toLowerCase() &&
                 Number(t.id) > 0 && !String(t.id).startsWith("local_")
        );

        if (existing && existing.id) {
          numericId = Number(existing.id);
        } else {
          const payload = {
            templateName: selectedTemplateCard.name,
            voucherType: selectedVoucherType,
            paperSize: selectedTemplateCard.paperSize || "58MM",
            widthMm: selectedTemplateCard.widthMm || 58,
            heightMm: selectedTemplateCard.heightMm || 130,
            paddingMm: 2,
            templateJson: templateJsonStr || "{}",
            createdBy: "admin"
          };
          if (apiBaseUrl && dbCode) {
            const saveRes = await axios.post(`${apiBaseUrl}/main/printtemplates/save/${dbCode}`, payload);
            if (saveRes.data?.templateId) {
              numericId = Number(saveRes.data.templateId);
              selectedTemplateCard.numericId = numericId;
              selectedTemplateCard.id = numericId;
              // Also update localStorage so next time the card already has a numeric id
              try {
                const localKey = `shergold_print_templates_${dbCode || "DEFAULT"}`;
                const localList = JSON.parse(localStorage.getItem(localKey) || "[]");
                const idx = localList.findIndex(
                  (t) => (t.templateName || "").trim().toLowerCase() === (selectedTemplateCard.name || "").trim().toLowerCase()
                );
                if (idx >= 0) {
                  localList[idx].id = numericId;
                  localList[idx].templateId = numericId;
                  localStorage.setItem(localKey, JSON.stringify(localList));
                }
              } catch (_) {}
              await fetchTemplates();
            } else {
              console.error("[Assign] Save did not return templateId. Full response:", saveRes.data);
            }
          }
        }
      }

      numericId = Number(numericId);
      if (!numericId || isNaN(numericId) || numericId <= 0) {
        showNotification("Could not resolve template identifier in database.", "error");
        setIsAssigning(false);
        return;
      }

      // Check if assigning to Global when showroom branch overrides exist
      if (Number(selectedLocation) === 0) {
        const branchOverrides = assignments.filter(
          (a) =>
            (a.VoucherType || "").trim().toUpperCase() === selectedVoucherType.toUpperCase() &&
            Number(a.Location) > 0
        );
        if (branchOverrides.length > 0) {
          const overrideNames = branchOverrides
            .map((b) => {
              const locObj = locationsList.find((l) => Number(l.id) === Number(b.Location));
              const locName = locObj ? locObj.name : `Branch #${b.Location}`;
              return `• <b>${locName}</b> (currently assigned to "${b.TemplateName || "Model #" + b.TemplateId}")`;
            })
            .join("<br/>");

          const res = await Swal.fire({
            title: "Branch Overrides Detected",
            html: `
              <div style="text-align: left; font-size: 13px; line-height: 1.5;">
                <p>You are assigning this model to <b>Global (All Showrooms)</b>.</p>
                <p style="margin-top: 8px;">However, the following showroom(s) currently have custom branch-specific assignments that will <b>override</b> the global setting:</p>
                <div style="margin: 10px 0; padding: 10px; background: #fff1f2; border: 1px solid #fecdd3; border-radius: 8px; color: #9f1239;">
                  ${overrideNames}
                </div>
                <p>Would you like to clear these branch overrides so <b>ALL showrooms</b> use this new model?</p>
              </div>
            `,
            icon: "warning",
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: "Apply to ALL (Clear Branch Overrides)",
            denyButtonText: "Keep Branch Overrides",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#0f2d59",
            denyButtonColor: "#64748b"
          });

          if (res.isDismissed) {
            setIsAssigning(false);
            return;
          }

          if (res.isConfirmed) {
            for (const bo of branchOverrides) {
              try {
                await axios.delete(
                  `${apiBaseUrl}/main/printtemplates/assignments/${dbCode}/${encodeURIComponent(selectedVoucherType)}?location=${bo.Location}`
                );
              } catch (_) {}
            }
          }
        }
      }

      // Assign in Backend
      if (apiBaseUrl && dbCode) {
        const assignRes = await axios.post(`${apiBaseUrl}/main/printtemplates/assign/${dbCode}`, {
          voucherType: selectedVoucherType,
          templateId: numericId,
          location: Number(selectedLocation) || 0,
          user: "admin"
        });
        if (assignRes.data && assignRes.data.success === false) {
          throw new Error(assignRes.data.message || "Assignment failed on server");
        }
      }

      // Invalidate template cache and notify all vouchers & tabs in real-time
      try {
        clearPrintTemplateCache();
        notifyPrintTemplateUpdated({
          dbCode: dbCode || "DEFAULT",
          voucherType: selectedVoucherType,
          templateId: numericId,
          location: Number(selectedLocation) || 0,
          action: "assign"
        });
      } catch (_) {}

      // Refresh assignments from backend
      await fetchAssignments(selectedLocation);

      showNotification(`Assigned "${selectedTemplateCard.name}" to ${selectedVoucherType}`, "success");
    } catch (err) {
      console.error("Assignment error:", err);
      showNotification(`Failed to assign: ${err.message}`, "error");
    } finally {
      setIsAssigning(false);
    }
  };

  /**
   * Remove / Reset current assignment for this voucher and showroom
   */
  const handleRemoveAssignment = async () => {
    if (!hasDeletePermission) {
      showNotification("You do not have permission to remove print model assignments.", "warning");
      return;
    }
    try {
      if (apiBaseUrl && dbCode) {
        await axios.delete(
          `${apiBaseUrl}/main/printtemplates/assignments/${dbCode}/${encodeURIComponent(selectedVoucherType)}?location=${selectedLocation}`
        );
      }
      const filtered = assignments.filter(
        (a) =>
          !(
            (a.VoucherType || "").trim().toUpperCase() === selectedVoucherType.toUpperCase() &&
            Number(a.Location) === Number(selectedLocation)
          )
      );
      setAssignments(filtered);
      localStorage.setItem(`shergold_print_assignments_${dbCode || "DEFAULT"}`, JSON.stringify(filtered));
      try {
        clearPrintTemplateCache();
        notifyPrintTemplateUpdated({
          dbCode: dbCode || "DEFAULT",
          voucherType: selectedVoucherType,
          location: Number(selectedLocation) || 0,
          action: "unassign"
        });
      } catch (_) {}
      showNotification(`Unassigned model from ${selectedVoucherType}`, "info");
    } catch (err) {
      console.error("Unassign error:", err);
      showNotification(`Failed to remove assignment: ${err.message}`, "error");
    }
  };

  /**
   * Determine if the currently previewed card is already the active assigned model
   */
  const isSelectedCardActive = useMemo(() => {
    if (!selectedTemplateCard || !activeAssignment) return false;
    return Number(selectedTemplateCard.numericId) === Number(activeAssignment.TemplateId);
  }, [selectedTemplateCard, activeAssignment]);

  // Current location name
  const currentLocationName = useMemo(() => {
    const loc = locationsList.find((l) => Number(l.id) === Number(selectedLocation));
    return loc ? loc.name : `Location #${selectedLocation}`;
  }, [locationsList, selectedLocation]);

  // Associated forms for selected voucher type
  const associatedForms = useMemo(() => {
    const vUpper = (selectedVoucherType || "").trim().toUpperCase();
    return registeredForms.filter(
      (f) => (f.fmrTypeOfVoucher || "").trim().toUpperCase() === vUpper
    );
  }, [registeredForms, selectedVoucherType]);

  const voucherMeta = useMemo(() => getVoucherMeta(selectedVoucherType), [selectedVoucherType]);

  return (
    <div
      className="absolute inset-0 w-full h-full bg-[#030914]/60 backdrop-blur-xs flex items-center justify-center pointer-events-auto select-none p-1 sm:p-3"
      style={{ zIndex: zIndex || 102 }}
    >
      <div
        className="bg-[#f8fafc] font-normal w-full max-w-[1440px] h-[98%] max-h-[920px] border border-slate-300/80 shadow-2xl rounded-2xl flex flex-col overflow-hidden relative"
        style={INTER}
      >
        {/* ── 1. Window Header ────────────────────────────────────────────────── */}
        <WindowHeader
          title="Print Model Assigner & Voucher Routing — SherGold Prime"
          logo={headeLogo}
          onClose={onClose}
          className="bg-gradient-to-r from-[#051126] via-[#0b2246] to-[#143260] text-white h-9 px-3 shrink-0"
          closeBtnClassName="h-6 w-6 my-auto mr-1 bg-red-600 hover:bg-red-700 rounded-sm border-none flex items-center justify-center text-white"
        />

        {/* ── 2. Top Banner & Global Selectors Bar ────────────────────────────── */}
        <div className="bg-white border-b border-slate-200/90 px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 shadow-2xs shrink-0">
          {/* Title & Badge */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#0f2d59] to-[#1e497d] flex items-center justify-center text-amber-300 shadow-xs border border-amber-400/20">
              <FiPrinter className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xs sm:text-sm font-extrabold text-slate-800 tracking-tight" style={INTER}>
                  Voucher Print Model Routing
                </h1>
                <span className="text-[9.5px] font-bold bg-amber-50 text-amber-800 border border-amber-300/60 px-2 py-0.5 rounded-full flex items-center gap-1">
                  ⭐ FORM REGISTRATION
                </span>
                <span className="text-[9.5px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full hidden sm:inline">
                  {voucherTypes.length} Voucher Types
                </span>
              </div>
              <p className="text-[10px] text-slate-500 font-medium">
                Map invoice and slip designs to transaction vouchers per showroom location with live PDF preview.
              </p>
            </div>
          </div>

          {/* Right Controls: Manual Guide Button & Showroom Dropdown */}
          <div className="flex items-center gap-2">
            {/* User Manual / Guide Button */}
            <button
              type="button"
              onClick={() => setShowManualModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 text-xs font-bold border border-purple-300 text-purple-900 transition shadow-2xs cursor-pointer active:scale-95"
              title="Open the complete User Manual & Guide for Model Assigner and Invoice Designer"
              style={INTER}
            >
              <FiHelpCircle className="w-3.5 h-3.5 text-purple-600" />
              <span>Guide / Manual</span>
            </button>

            {/* Showroom Branch Location Dropdown */}
            <div className="flex items-center gap-2 bg-slate-50 hover:bg-slate-100/80 px-3 py-1.5 rounded-xl border border-slate-200 transition-colors">
              <FiHome className="w-3.5 h-3.5 text-[#0f2d59]" />
              <span className="text-[11px] font-bold text-slate-600">Showroom:</span>
              <select
                value={selectedLocation}
                onChange={(e) => {
                  const loc = Number(e.target.value);
                  setSelectedLocation(loc);
                  fetchAssignments(loc);
                }}
                className="bg-white border border-slate-300 text-slate-900 text-xs font-bold rounded-lg px-2.5 py-1 focus:outline-none focus:border-[#0f2d59] cursor-pointer shadow-2xs"
                style={INTER}
              >
                {locationsList.map((loc) => {
                  const isCurrent = currentLocId > 0 && Number(loc.id) === currentLocId;
                  return (
                    <option key={loc.id} value={loc.id}>
                      {loc.name} {isCurrent ? "★ (Current Showroom)" : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        {/* ── 3. Main Split View: Left (Card Selector) & Right (Live Preview) ──── */}
        <div className="flex-1 p-2 sm:p-3 flex flex-col lg:flex-row gap-3 min-h-0 overflow-hidden">
          
          {/* ══════════════════════════════════════════════════════════════════════
              LEFT COLUMN: VOUCHER PICKER + 2-COLUMN LUXURY CARD SELECTOR
             ══════════════════════════════════════════════════════════════════════ */}
          <div className="w-full lg:w-[48%] xl:w-[46%] bg-white border border-slate-200/90 rounded-2xl shadow-xs flex flex-col overflow-hidden">
            
            {/* Voucher Selection Bar */}
            <div className="p-3 border-b border-slate-100 bg-slate-50/70 shrink-0 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5 uppercase tracking-wider" style={INTER}>
                  <FiTag className="w-3.5 h-3.5 text-[#0f2d59]" />
                  Transaction Voucher Type:
                  {customVoucherTypes.includes(selectedVoucherType) && (
                    <span className="text-[9px] font-bold bg-purple-100 text-purple-800 border border-purple-200 px-1.5 py-0.2 rounded-full uppercase tracking-wider ml-1">
                      ⭐ Custom Voucher
                    </span>
                  )}
                </label>
                {isLoadingVouchers && (
                  <span className="text-[10px] text-amber-700 animate-pulse font-medium">
                    Loading vouchers...
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* ── DUAL-MODE COMBOBOX: Editable Input Field + Dropdown Select at the same time ── */}
                <div className="relative flex-1" ref={voucherComboboxRef}>
                  <div className="relative flex items-center">
                    <input
                      type="text"
                      value={voucherInputText}
                      onChange={(e) => {
                        const val = e.target.value;
                        setVoucherInputText(val);
                        setSelectedVoucherType(val.toUpperCase());
                        setIsVoucherDropdownOpen(true);
                      }}
                      onFocus={() => setIsVoucherDropdownOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSelectVoucherType(voucherInputText);
                        } else if (e.key === "Escape") {
                          setIsVoucherDropdownOpen(false);
                        }
                      }}
                      placeholder="Type custom voucher name or select from list..."
                      className="w-full h-8 pl-3 pr-14 border border-slate-300 rounded-lg text-slate-800 text-xs font-bold bg-white focus:outline-none focus:border-[#0f2d59] focus:ring-1 focus:ring-[#0f2d59]/20 shadow-2xs uppercase placeholder:normal-case placeholder:font-normal tracking-wide"
                      style={INTER}
                    />

                    {/* Clear text icon button */}
                    {voucherInputText ? (
                      <button
                        type="button"
                        onClick={() => {
                          setVoucherInputText("");
                          setIsVoucherDropdownOpen(true);
                        }}
                        className="absolute right-7 p-1 text-slate-400 hover:text-slate-600 transition cursor-pointer"
                        title="Clear voucher input"
                      >
                        <FiX className="w-3 h-3" />
                      </button>
                    ) : null}

                    {/* Chevron toggle button */}
                    <button
                      type="button"
                      onClick={() => setIsVoucherDropdownOpen((prev) => !prev)}
                      className="absolute right-1 w-6 h-6 flex items-center justify-center text-slate-500 hover:text-slate-800 rounded transition cursor-pointer"
                      title="Open voucher list"
                    >
                      {isVoucherDropdownOpen ? (
                        <FiChevronUp className="w-3.5 h-3.5" />
                      ) : (
                        <FiChevronDown className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>

                  {/* Combobox Dropdown Menu */}
                  {isVoucherDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 max-h-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-y-auto py-1">
                      {/* Dynamic "+ Use as custom voucher" if user typed something new */}
                      {voucherInputText.trim() &&
                        !voucherTypes.some((v) => v.toUpperCase() === voucherInputText.trim().toUpperCase()) && (
                          <div
                            onClick={() => handleSelectVoucherType(voucherInputText)}
                            className="px-3 py-2 text-xs font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border-b border-emerald-100 flex items-center gap-1.5 cursor-pointer transition"
                          >
                            <FiPlus className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span className="truncate">
                              Add & Use Custom: <strong className="underline uppercase">{voucherInputText.trim()}</strong>
                            </span>
                          </div>
                        )}

                      {/* Matching Vouchers List */}
                      {filteredDropdownVouchers.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-slate-400 italic">
                          No matching vouchers found. Press Enter to use "{voucherInputText.trim().toUpperCase()}" as custom.
                        </div>
                      ) : (
                        filteredDropdownVouchers.map((vType) => {
                          const isSelected = (selectedVoucherType || "").toUpperCase() === vType.toUpperCase();
                          const isCustom = customVoucherTypes.includes(vType);
                          const formsCount = registeredForms.filter(
                            (f) => (f.fmrTypeOfVoucher || "").toUpperCase() === vType.toUpperCase()
                          ).length;

                          return (
                            <div
                              key={vType}
                              onClick={() => handleSelectVoucherType(vType)}
                              className={`group px-3 py-1.5 text-xs font-bold flex items-center justify-between cursor-pointer transition ${
                                isSelected
                                  ? "bg-[#0f2d59] text-white"
                                  : "text-slate-800 hover:bg-slate-100"
                              }`}
                            >
                              <div className="flex items-center gap-2 truncate">
                                <span className="truncate">{vType}</span>
                                {isCustom ? (
                                  <span
                                    className={`text-[8.5px] px-1.5 py-0.2 rounded font-extrabold uppercase tracking-wider shrink-0 ${
                                      isSelected
                                        ? "bg-white/20 text-white"
                                        : "bg-purple-100 text-purple-800 border border-purple-200"
                                    }`}
                                  >
                                    Custom
                                  </span>
                                ) : formsCount > 0 ? (
                                  <span
                                    className={`text-[8.5px] px-1.5 py-0.2 rounded font-medium shrink-0 ${
                                      isSelected ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                                    }`}
                                  >
                                    {formsCount} {formsCount === 1 ? "Form" : "Forms"}
                                  </span>
                                ) : null}
                              </div>

                              <div className="flex items-center gap-1">
                                {isSelected && <FiCheck className="w-3.5 h-3.5 shrink-0 stroke-[2.5]" />}
                                {isCustom && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteCustomVoucherType(vType);
                                    }}
                                    title={`Remove custom voucher "${vType}"`}
                                    className={`p-1 rounded transition opacity-60 group-hover:opacity-100 ${
                                      isSelected ? "hover:bg-red-500 text-white" : "hover:bg-red-50 text-red-500"
                                    }`}
                                  >
                                    <FiTrash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                {/* Reload Button */}
                <button
                  type="button"
                  onClick={fetchVoucherTypesFromDb}
                  title="Reload Vouchers from FormRegistration and database"
                  className="h-8 px-2.5 bg-white hover:bg-slate-100 border border-slate-300 rounded-lg text-slate-600 text-xs font-bold flex items-center gap-1 transition cursor-pointer shrink-0 shadow-2xs"
                >
                  <FiRefreshCw className="w-3 h-3" />
                  Reload
                </button>
              </div>

              {/* Associated registered forms pills */}
              {associatedForms.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                  <span className="text-[9px] font-bold text-slate-400 uppercase">
                    Forms ({associatedForms.length}):
                  </span>
                  {associatedForms.slice(0, 3).map((f) => (
                    <span
                      key={f.fmrEntryNo}
                      className="text-[9.5px] font-medium bg-slate-200/80 text-slate-700 px-2 py-0.5 rounded-md truncate max-w-[140px]"
                      title={`${f.fmrName} (${f.fmrAbbreviation})`}
                    >
                      {f.fmrAbbreviation || f.fmrName}
                    </span>
                  ))}
                  {associatedForms.length > 3 && (
                    <span className="text-[9px] text-slate-400 font-semibold">
                      +{associatedForms.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Template Search & Category Filter Sub-bar */}
            <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between gap-2 shrink-0 bg-white">
              <div className="relative flex-1">
                <FiSearch className="w-3 h-3 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={templateSearchQuery}
                  onChange={(e) => setTemplateSearchQuery(e.target.value)}
                  placeholder="Search template name or size..."
                  className="w-full h-7 pl-7 pr-2 border border-slate-200 rounded-lg text-[11px] text-slate-800 focus:outline-none focus:border-[#0f2d59] bg-slate-50/50"
                  style={INTER}
                />
              </div>

              {/* Paper size pills */}
              <div className="flex items-center gap-1 shrink-0">
                {[
                  { id: "all", label: "All" },
                  { id: "thermal", label: "Thermal (Roll)" },
                  { id: "sheet", label: "Sheet (A4)" }
                ].map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setPaperFilter(f.id)}
                    className={`px-2 py-0.5 text-[10px] font-bold rounded-md transition cursor-pointer ${
                      paperFilter === f.id
                        ? "bg-[#0f2d59] text-white shadow-2xs"
                        : "bg-slate-100 hover:bg-slate-200 text-slate-600"
                    }`}
                    style={INTER}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Section Header */}
            <div className="px-3 pt-2.5 pb-1 flex items-center justify-between shrink-0">
              <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5" style={INTER}>
                <FiPrinter className="w-3.5 h-3.5 text-amber-600" />
                Available Models for {selectedVoucherType}
              </span>
              <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" style={INTER}>
                {filteredTemplates.length} Models Found
              </span>
            </div>

            {/* ── 2-COLUMN LUXURY TEMPLATE CARD SELECTOR (AppConfigMaster pattern) ── */}
            <div className="flex-1 overflow-y-auto p-3 min-h-0">
              {filteredTemplates.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-6 text-slate-400">
                  <FiPrinter className="w-10 h-10 stroke-[1.2] text-slate-300 mb-2" />
                  <p className="text-xs font-bold text-slate-600">No print models found for this voucher type</p>
                  <p className="text-[11px] text-slate-400 mt-1 max-w-[280px]">
                    Create a custom design in Invoice Designer or select a standard preset to get started.
                  </p>
                  <button
                    type="button"
                    onClick={() => onOpenDesigner && onOpenDesigner(selectedVoucherType)}
                    className="mt-3 px-3 py-1.5 bg-[#0f2d59] hover:bg-[#1a3d70] text-white text-xs font-bold rounded-lg flex items-center gap-1.5 transition shadow-xs cursor-pointer"
                    style={INTER}
                  >
                    <FiPlus className="w-3.5 h-3.5" />
                    Open Invoice Designer
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredTemplates.map((tpl) => {
                    const isSelected = selectedTemplateCard?.id === tpl.id;
                    const isAssigned =
                      activeAssignment?.TemplateId &&
                      Number(tpl.numericId) === Number(activeAssignment.TemplateId);

                    const isThermal =
                      tpl.paperSize.includes("58") ||
                      tpl.paperSize.includes("57") ||
                      tpl.paperSize.includes("80");

                    return (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => setSelectedTemplateCard(tpl)}
                        className={`group relative flex flex-col p-2.5 rounded-2xl border transition-all duration-200 cursor-pointer select-none text-left ${
                          isSelected
                            ? "border-[#0f2d59] bg-white ring-3 ring-[#0f2d59]/20 shadow-md transform -translate-y-0.5"
                            : "border-slate-200/90 bg-white hover:border-[#b78a39]/70 hover:shadow-md hover:-translate-y-0.5"
                        }`}
                      >
                        {/* Miniature Card Mockup Canvas */}
                        <div
                          className="w-full rounded-xl relative overflow-hidden border border-black/10 shadow-2xs transition-transform h-[88px] p-2 flex flex-col justify-between"
                          style={{ background: tpl.bgGradient }}
                        >
                          {/* Top rule & watermark lines simulating receipt */}
                          <div className="flex items-center justify-between border-b border-slate-300/60 pb-1">
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-amber-500/80" />
                              <span className="text-[7.5px] font-mono font-bold tracking-wider text-slate-600 uppercase">
                                {isThermal ? "POS TICKET" : "TAX INVOICE"}
                              </span>
                            </div>
                            <span className="text-[8px] font-mono font-bold text-slate-500">
                              {tpl.paperSize}
                            </span>
                          </div>

                          {/* Center Graphic: Simulated Invoice Lines / Barcode */}
                          <div className="flex flex-col gap-1 my-auto opacity-70">
                            <div className="h-1 bg-slate-300 rounded w-4/5" />
                            <div className="h-1 bg-slate-300 rounded w-3/5" />
                            <div className="flex items-center gap-1 mt-0.5">
                              <div className="h-2.5 w-6 bg-slate-400/80 rounded-xs" />
                              <div className="h-1 bg-slate-300 rounded flex-1" />
                            </div>
                          </div>

                          {/* Bottom dimensions watermark */}
                          <div className="flex items-center justify-between text-[8px] text-slate-500 font-mono">
                            <span>{tpl.widthMm} × {tpl.heightMm} mm</span>
                            <span>{tpl.isPreset ? "PRESET" : "CUSTOM"}</span>
                          </div>

                          {/* Active Assignment Floating Badge */}
                          {isAssigned && (
                            <div className="absolute top-1.5 right-1.5 z-20 flex items-center gap-1 bg-emerald-600 text-white px-2 py-0.5 rounded-full text-[8px] font-extrabold shadow-sm border border-white/40 tracking-wider">
                              <FiCheck className="w-2.5 h-2.5 stroke-[3]" />
                              <span>ACTIVE</span>
                            </div>
                          )}

                          {/* Selected ring overlay */}
                          {isSelected && !isAssigned && (
                            <div className="absolute top-1.5 right-1.5 z-20 flex items-center gap-1 bg-[#0f2d59] text-white px-1.5 py-0.5 rounded-full text-[7.5px] font-bold tracking-wider">
                              <span>PREVIEWING</span>
                            </div>
                          )}
                        </div>

                        {/* Card Information Footer */}
                        <div className="flex flex-col w-full px-0.5 pt-2">
                          <div className="flex items-center justify-between gap-1.5">
                            <span
                              className={`text-[12px] font-extrabold leading-tight truncate ${
                                isSelected ? "text-[#0f2d59]" : "text-slate-800"
                              }`}
                              style={INTER}
                              title={tpl.name}
                            >
                              {tpl.name}
                            </span>
                            <span
                              className={`text-[8.5px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 leading-none ${
                                isSelected
                                  ? "bg-[#0f2d59]/10 text-[#0f2d59]"
                                  : "bg-slate-100 text-slate-600"
                              }`}
                              style={INTER}
                            >
                              {tpl.paperSize}
                            </span>
                          </div>

                          <div className="flex items-center justify-between text-[10px] text-slate-500 font-medium truncate mt-1">
                            <span>{tpl.widthMm} × {tpl.heightMm} mm</span>
                            <span className={tpl.isPreset ? "text-blue-700 font-bold" : "text-amber-700 font-bold"}>
                              {tpl.isPreset ? "Factory Preset" : "Custom Model"}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bottom Actions Footer */}
            <div className="p-3 border-t border-slate-100 flex items-center justify-between gap-2 shrink-0 bg-slate-50/50">
              <button
                type="button"
                onClick={() => onOpenDesigner && onOpenDesigner(selectedVoucherType)}
                className="px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 rounded-lg text-xs font-bold flex items-center gap-1.5 transition cursor-pointer shadow-2xs"
                style={INTER}
              >
                <FiPlus className="w-3.5 h-3.5 text-[#0f2d59]" />
                New in Designer
              </button>

              <button
                type="button"
                onClick={fetchTemplates}
                className="px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-600 border border-slate-300 rounded-lg text-xs font-bold flex items-center gap-1.5 transition cursor-pointer shadow-2xs"
                style={INTER}
              >
                <FiRefreshCw className="w-3.5 h-3.5" />
                Refresh Models
              </button>
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════════════
              RIGHT COLUMN: LIVE PDF PREVIEW BEFORE SELECTION + 1-CLICK ASSIGN
             ══════════════════════════════════════════════════════════════════════ */}
          <div className="flex-1 bg-white border border-slate-200/90 rounded-2xl shadow-xs flex flex-col overflow-hidden">
            
            {/* Live Preview Header */}
            <div className="p-3.5 border-b border-slate-200/80 flex flex-wrap items-center justify-between gap-3 bg-white shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shadow-2xs border border-emerald-200/80">
                  <FiEye className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xs sm:text-sm font-bold text-slate-800 leading-none" style={INTER}>
                      Live Preview — {selectedTemplateCard?.name || "No Template Selected"}
                    </h3>
                    {isSelectedCardActive && (
                      <span className="text-[9.5px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <FiCheck className="w-2.5 h-2.5 stroke-[3]" /> ACTIVE DEFAULT
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-slate-400 font-medium mt-1 block" style={INTER}>
                    Voucher: <strong className="text-slate-700 font-bold">{selectedVoucherType}</strong> • {selectedTemplateCard?.widthMm} × {selectedTemplateCard?.heightMm} mm ({selectedTemplateCard?.paperSize})
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                {selectedTemplateCard && (
                  <button
                    type="button"
                    onClick={() =>
                      onOpenDesigner &&
                      onOpenDesigner(selectedVoucherType, selectedTemplateCard.numericId)
                    }
                    className="px-2.5 py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-300 rounded-lg text-xs font-bold flex items-center gap-1.5 transition cursor-pointer shadow-2xs"
                    style={INTER}
                    title="Open this template in visual Invoice Designer"
                  >
                    <FiEdit3 className="w-3.5 h-3.5 text-blue-600" />
                    Open in Designer
                  </button>
                )}

                {/* Primary: Set as Active Model */}
                {(hasSavePermission || hasEditPermission) && (
                  <button
                    type="button"
                    onClick={handleAssignCurrentTemplate}
                    disabled={isAssigning || isSelectedCardActive || !selectedTemplateCard}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-xs transition ${
                      isSelectedCardActive
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default"
                        : isAssigning
                        ? "bg-slate-300 text-slate-500 cursor-wait"
                        : "bg-[#0f2d59] hover:bg-[#1a3d70] text-white cursor-pointer"
                    }`}
                    style={INTER}
                  >
                    <FiCheck className="w-3.5 h-3.5 stroke-[2.5]" />
                    {isSelectedCardActive
                      ? "Currently Assigned"
                      : isAssigning
                      ? "Assigning..."
                      : "Assign as Active Model"}
                  </button>
                )}
              </div>
            </div>

            {/* ── LIVE PREVIEW CANVAS AREA ── */}
            <div className="flex-1 bg-[#f4f7fb] flex flex-col items-center justify-center p-3 sm:p-4 min-h-0 overflow-y-auto relative">
              {isPreviewLoading && (
                <div className="absolute inset-0 bg-white/70 backdrop-blur-2xs flex flex-col items-center justify-center z-10">
                  <div className="w-8 h-8 border-3 border-[#0f2d59] border-t-transparent rounded-full animate-spin mb-2" />
                  <span className="text-xs font-bold text-slate-700" style={INTER}>
                    Compiling High-Precision PDF Preview...
                  </span>
                  <span className="text-[10px] text-slate-400 mt-0.5">
                    Generating mock jewellery invoice with barcodes & tables
                  </span>
                </div>
              )}

              {previewError ? (
                <div className="p-6 text-center max-w-[360px] bg-white rounded-2xl border border-rose-200 shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-2">
                    <FiInfo className="w-5 h-5" />
                  </div>
                  <h4 className="text-xs font-bold text-slate-800">Preview Generation Error</h4>
                  <p className="text-[11px] text-slate-500 mt-1">{previewError}</p>
                </div>
              ) : previewPdfUrl ? (
                <div className="w-full h-full max-w-[850px] flex flex-col items-center justify-center">
                  <iframe
                    src={`${previewPdfUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                    className="w-full h-full rounded-xl border border-slate-300/80 shadow-md bg-white"
                    title="Live Print Preview"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-slate-400 p-8 text-center">
                  <FiEye className="w-12 h-12 stroke-[1.2] text-slate-300 mb-2" />
                  <p className="text-xs font-bold text-slate-600">Select a template from the left to preview</p>
                  <p className="text-[11px] text-slate-400 mt-1 max-w-[280px]">
                    The live document canvas will render here before you assign it.
                  </p>
                </div>
              )}
            </div>

            {/* Bottom Status & Summary Bar */}
            <div className="p-2.5 px-4 bg-white border-t border-slate-200/90 flex flex-wrap items-center justify-between gap-3 shrink-0 text-xs">
              <div className="flex items-center gap-2">
                <FiInfo className="w-3.5 h-3.5 text-[#0f2d59] shrink-0" />
                <span className="text-[11.5px] text-slate-600" style={INTER}>
                  Showroom: <strong>{currentLocationName}</strong> | Voucher: <strong>{selectedVoucherType}</strong>
                </span>
                {activeAssignment ? (
                  <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                    Assigned: {activeAssignment.TemplateName || `Model #${activeAssignment.TemplateId}`}
                    {activeAssignment.isInheritedFromGlobal ? " (Inherited from Global)" : ""}
                  </span>
                ) : (
                  <span className="text-[11px] text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 font-medium">
                    No custom assignment (Using default)
                  </span>
                )}
              </div>

              {activeAssignment && !activeAssignment.isInheritedFromGlobal && hasDeletePermission && (
                <button
                  type="button"
                  onClick={handleRemoveAssignment}
                  className="px-2 py-1 text-[11px] font-bold text-rose-600 hover:text-rose-700 hover:bg-rose-50 border border-rose-200 rounded-md transition cursor-pointer flex items-center gap-1"
                  style={INTER}
                  title="Remove assignment override for this showroom location"
                >
                  <FiTrash2 className="w-3 h-3" />
                  Reset to Default
                </button>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 📖 INTERACTIVE USER MANUAL & COMPONENT GUIDE MODAL                   */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <InvoiceDesignerManualModal
        isOpen={showManualModal}
        onClose={() => setShowManualModal(false)}
        zIndex={(zIndex || 100) + 50}
      />
    </div>
  );
}
